import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Input, Switch, Button, Row, Col, Typography, Space } from "antd";
import { toast } from "../../components/toasts/toastStore";
import SuccessOverlay from "../../components/feedback/SuccessOverlay";
import { apiLentes, authHeader } from "../../redux/actions/catalogos/catalogosApi";
import PacienteFotoPicker from "./PacienteFotoPicker";
import { fotoUrl } from "./EntidadFotoCard";

import "./PacientesPage.css";

const { Title, Text } = Typography;

export default function CrearLentePage() {
  const navigate = useNavigate();
  const { idLente } = useParams();
  const isEditing = !!idLente;

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingItem, setLoadingItem] = useState(isEditing);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoActual, setFotoActual] = useState(null);
  const [success, setSuccess] = useState({ show: false, title: "", subtitle: "", to: "/app/lentes" });

  useEffect(() => {
    if (!isEditing) return;
    apiLentes
      .get(`/lentes/${idLente}`, { headers: authHeader() })
      .then((res) => {
        const item = res.data ?? {};
        form.setFieldsValue({
          nombre_modelo: item.nombre_modelo,
          poder: item.poder,
          proveedor: item.proveedor,
          maneja_inventario: Boolean(item.maneja_inventario),
        });
        setFotoActual(fotoUrl(item));
      })
      .catch(() => toast.error("No se pudo cargar el lente"))
      .finally(() => setLoadingItem(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idLente]);

  const subirFoto = async (id) => {
    if (!fotoFile) return;
    const fd = new FormData();
    fd.append("file", fotoFile);
    try {
      await apiLentes.post(`/lentes/${id}/foto`, fd, { headers: authHeader() });
    } catch (err) {
      toast.warning(err?.response?.data?.detail || "El lente se guardó, pero no se pudo subir la imagen");
    }
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload = {
      nombre_modelo: values.nombre_modelo?.trim(),
      poder: values.poder?.trim() || null,
      proveedor: values.proveedor?.trim() || null,
      maneja_inventario: Boolean(values.maneja_inventario),
    };

    setSaving(true);
    try {
      if (isEditing) {
        await apiLentes.put(`/lentes/${idLente}`, payload, { headers: authHeader() });
        await subirFoto(idLente);
        setSuccess({ show: true, title: "Lente actualizado", subtitle: "Los cambios se guardaron correctamente.", to: `/app/lentes/${idLente}` });
      } else {
        const { data } = await apiLentes.post("/lentes", payload, { headers: authHeader() });
        await subirFoto(data?.id);
        setSuccess({ show: true, title: "Lente creado", subtitle: "Se agregó al catálogo correctamente.", to: `/app/lentes/${data?.id}` });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "No se pudo guardar el lente");
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="paq-title" style={{ marginBottom: 0 }}>
              {isEditing ? "Editando lente" : "Nuevo lente"}
            </Title>
            <Text className="paq-subtitle">
              {isEditing
                ? "Modifica los datos del modelo y guarda los cambios."
                : "Completa los datos para agregar el modelo al catálogo de lentes."}
            </Text>
          </Space>

          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate(-1)} disabled={saving || success.show}>
              Cancelar
            </Button>
            <Button
              type="primary"
              loading={saving}
              disabled={success.show || loadingItem}
              icon={!isEditing ? <i className="fa-solid fa-plus" /> : null}
              onClick={handleSave}
              style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
            >
              {isEditing ? "Guardar cambios" : "Crear lente"}
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical" initialValues={{ maneja_inventario: true }} disabled={loadingItem}>
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-glasses" /></span>
              Datos del lente
            </div>

            <Row gutter={16}>
              <Col xs={24}>
                <div className="paq-field-label">Imagen del lente (opcional)</div>
                <PacienteFotoPicker value={fotoActual} onChange={setFotoFile} icon="fa-glasses" />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="nombre_modelo"
                  label={<span className="paq-field-label">Nombre del modelo</span>}
                  rules={[{ required: true, whitespace: true, message: "Requerido" }]}
                >
                  <Input placeholder="Ej. Ray-Ban Aviator RB3025" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="poder" label={<span className="paq-field-label">Poder (opcional)</span>}>
                  <Input placeholder="Ej. -2.50" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="proveedor" label={<span className="paq-field-label">Proveedor (opcional)</span>}>
                  <Input placeholder="Nombre del proveedor" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="maneja_inventario" label={<span className="paq-field-label">Maneja inventario</span>} valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>

      <SuccessOverlay
        show={success.show}
        title={success.title}
        subtitle={success.subtitle}
        onDone={() => navigate(success.to)}
      />
    </main>
  );
}
